import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOJsonLd from '@/components/feature/SEOJsonLd';
import { testimonials } from '@/mocks/testimonials';
import { faqs } from '@/mocks/faqs';

const faqJsonLd = {
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const orgJsonLd = {
  name: '韩漫网站',
  url: 'http://guyunqutang.com/',
  description: '韩漫网站提供在线韩国漫画、好看的韩漫免费在线观看，内容新颖画质佳。',
  inLanguage: 'zh-CN',
};


export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const res = await fetch('http://guyunqutang.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormError(serverMsg);
          setFormStatus('error');
        } else {
          setFormError(serverMsg);
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOJsonLd type="Organization" data={orgJsonLd} />
      <SEOJsonLd type="WebPage" data={{
        name: '关于我们 - 韩漫网站',
        description: '了解韩漫网站的品牌故事、平台特色与优势，以及用户口碑评价。韩漫网站致力于为中文读者提供最新最全的韩国漫画资源。',
        url: 'http://guyunqutang.com/about',
        inLanguage: 'zh-CN',
      }} />
      <SEOJsonLd type="FAQPage" data={faqJsonLd} />

      <header>
        <Navbar />
      </header>

      <main>
        {/* Brand Narrative */}
        <section className="pt-28 pb-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mb-10 mt-8">
              <img
                src="https://readdy.ai/api/search-image?query=Artistic%20illustration%20of%20stacked%20colorful%20comic%20books%20and%20manhwa%20volumes%20on%20a%20cozy%20desk%2C%20warm%20ambient%20lighting%2C%20reading%20glasses%20nearby%2C%20soft%20watercolor%20style%2C%20Korean%20aesthetic%2C%20minimalist%20composition%2C%20warm%20cream%20tones%2C%20no%20people&width=400&height=300&seq=about-illust-2026&orientation=landscape"
                alt="韩漫网站品牌插图"
                className="mx-auto w-full max-w-[400px] h-auto rounded-xl"
              />
            </div>
            <p className="text-xs text-foreground-500 uppercase tracking-wider mb-6">我们的故事</p>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 leading-relaxed max-w-2xl mx-auto">
              韩漫网站从热爱出发，致力于为每一位漫画爱好者打造一个纯净、便捷、高品质的在线阅读空间。我们相信，好的故事值得被更多人看见。
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-xl mx-auto">
              无论是恋爱甜宠、热血战斗还是悬疑推理，在这片属于韩漫的世界里，总有属于你的那份感动 <span role="img" aria-label="heart">💛</span>
            </p>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className={`flex flex-col lg:flex-row gap-8 items-center ${
                  i > 0 ? 'mt-12 pt-12 border-t border-background-200/60' : ''
                }`}
              >
                <div className="lg:w-[200px] flex-shrink-0">
                  <img
                    src={t.avatarImage}
                    alt={t.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover mx-auto"
                  />
                </div>
                <div className="flex-1">
                  <i className="ri-double-quotes-l text-4xl text-foreground-200 block mb-3"></i>
                  <p className="text-base md:text-lg text-foreground-800 leading-relaxed mb-4">
                    {t.content}
                  </p>
                  <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-start gap-8 mb-12">
              <div className="lg:w-1/3">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                  为什么选择
                  <br />
                  韩漫网站
                </h2>
              </div>
              <p className="lg:w-2/3 text-sm text-foreground-600 leading-relaxed lg:pt-2">
                我们不仅仅是一个漫画平台，更是一个为中文读者精心打造的韩漫社区。从内容筛选到阅读体验，每一个环节都倾注了我们的热情与专业。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-background-900 rounded-xl p-6 text-background-50">
                <div className="w-10 h-10 rounded-xl bg-background-50/10 flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                <h3 className="text-lg font-heading font-bold mb-2">正版授权</h3>
                <p className="text-sm text-background-300 leading-relaxed">
                  与多家韩国漫画平台合作，确保每一部作品都有合法授权，让您安心阅读。
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-image-line text-xl text-accent-700"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">高清画质</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  所有漫画均采用高清扫描，画面细节纤毫毕现，为您呈现最佳的视觉享受。
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mb-4">
                  <i className="ri-flashlight-line text-xl text-secondary-700"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">极速更新</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  热门连载同步更新，再也不用担心追更落后，第一时间享受最新话内容。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq) => (
                <details key={faq.id} className="group bg-background-50 rounded-xl border border-background-200/60">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i className="ri-add-line text-foreground-400 group-open:hidden text-sm flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block text-sm flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
                  有任何问题或建议？欢迎随时联系我们，我们会在24小时内回复您。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">hello@guyunqutang.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-800">中国上海市浦东新区张江高科技园区</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                 
                  className="bg-background-100 rounded-xl p-6 border border-background-200/60"
                >
                  <div className="honeypot-wrap">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/60 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/60 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/60 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-primary-500 text-background-50 text-sm font-semibold py-3 rounded-full hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-xs text-accent-700 text-center">留言发送成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-red-600 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}